import { baseApi } from '@/shared/api';
import type {
  AppBi,
  AppDetail,
  AppsSummary,
  BillingMixItem,
  Cohort,
  FacetsRequest,
  FacetsResult,
  FieldDef,
  KpiSummary,
  LifecycleCount,
  ListEntity,
  Movement,
  MrrSeries,
  OffersReport,
  PaymentsReport,
  PeriodQuery,
  PortalCatalog,
  PortalLoadStatus,
  PortalMeta,
  RenewalsReport,
  Scope,
  SearchRequest,
  SearchResult,
  SubscriptionDetail,
  TotalsReport,
  TrialFunnel,
  UserDetail,
  UsersSummary,
  ViewFilter,
} from './types';

/**
 * Portal data changes only when the backend rebuilds its snapshot (`meta.generatedAt`, by default
 * every 30 min). Responses are kept that long; a new snapshot invalidates the `Portal` tag
 * (see `portalListeners`).
 */
const SNAPSHOT_CACHE_SECONDS = 30 * 60;
const SNAPSHOT_DATA = {
  providesTags: ['Portal'],
  keepUnusedDataFor: SNAPSHOT_CACHE_SECONDS,
} as const;

/**
 * `meta` waits for the backend's data load after a start (about 1.5–2.5 min, see `getLoadStatus`): longer than the
 * default API timeout, so it gets its own.
 */
const META_TIMEOUT_MS = 5 * 60_000;

const scopeParams = ({ offer, billing }: Scope) => ({ offer, billing });

/** KPI portal endpoints (/api/portal): server-side aggregation of Stripe and Open as App data. */
export const portalApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getMeta: build.query<PortalMeta, undefined>({
      query: () => ({ url: '/api/portal/meta', timeout: META_TIMEOUT_MS }),
      keepUnusedDataFor: SNAPSHOT_CACHE_SECONDS,
    }),
    // Answers at once, also while `meta` waits for the data load; polled only while a page waits.
    getLoadStatus: build.query<PortalLoadStatus, undefined>({
      query: () => '/api/portal/status',
      keepUnusedDataFor: 0,
    }),
    getCatalog: build.query<PortalCatalog, undefined>({
      query: () => '/api/portal/catalog',
      ...SNAPSHOT_DATA,
    }),
    getKpis: build.query<KpiSummary, PeriodQuery>({
      query: (params) => ({ url: '/api/portal/metrics/kpis', params }),
      ...SNAPSHOT_DATA,
    }),
    getMrrSeries: build.query<MrrSeries, PeriodQuery>({
      query: (params) => ({ url: '/api/portal/metrics/mrr', params }),
      ...SNAPSHOT_DATA,
    }),
    getMovements: build.query<Movement[], Scope & { month: string }>({
      query: ({ month, ...scope }) => ({
        url: '/api/portal/metrics/movements',
        params: { month, ...scopeParams(scope) },
      }),
      ...SNAPSHOT_DATA,
    }),
    getBillingMix: build.query<BillingMixItem[], PeriodQuery>({
      query: (params) => ({ url: '/api/portal/metrics/billing-mix', params }),
      ...SNAPSHOT_DATA,
    }),
    getLifecycle: build.query<LifecycleCount[], Scope>({
      query: (scope) => ({ url: '/api/portal/metrics/lifecycle', params: scopeParams(scope) }),
      ...SNAPSHOT_DATA,
    }),
    getTrials: build.query<TrialFunnel, PeriodQuery>({
      query: ({ from, to }) => ({ url: '/api/portal/metrics/trials', params: { from, to } }),
      ...SNAPSHOT_DATA,
    }),
    /** Payments ignore the offer/billing scope: charges belong to customers, not offers. */
    getPayments: build.query<PaymentsReport, PeriodQuery>({
      query: ({ from, to, compare }) => ({
        url: '/api/portal/metrics/payments',
        params: { from, to, compare },
      }),
      ...SNAPSHOT_DATA,
    }),
    getOffers: build.query<OffersReport, PeriodQuery>({
      query: (params) => ({ url: '/api/portal/metrics/offers', params }),
      ...SNAPSHOT_DATA,
    }),
    getCohorts: build.query<Cohort[], Scope>({
      query: (scope) => ({ url: '/api/portal/metrics/cohorts', params: scopeParams(scope) }),
      ...SNAPSHOT_DATA,
    }),
    getRenewals: build.query<RenewalsReport, Scope & { days: number }>({
      query: ({ days, ...scope }) => ({
        url: '/api/portal/renewals',
        params: { days, ...scopeParams(scope) },
      }),
      ...SNAPSHOT_DATA,
    }),
    getFields: build.query<FieldDef[], ListEntity>({
      query: (entity) => ({ url: '/api/portal/views/fields', params: { entity } }),
      ...SNAPSHOT_DATA,
    }),
    searchList: build.query<SearchResult, { entity: ListEntity; request: SearchRequest }>({
      query: ({ entity, request }) => ({
        url: `/api/portal/${entity}/search`,
        method: 'POST',
        body: request,
      }),
      ...SNAPSHOT_DATA,
    }),
    countViews: build.query<
      Record<string, number>,
      { entity: ListEntity; scope: Scope; views: { id: string; filters: ViewFilter[] }[] }
    >({
      query: ({ entity, scope, views }) => ({
        url: `/api/portal/${entity}/counts`,
        method: 'POST',
        body: { scope, views },
      }),
      ...SNAPSHOT_DATA,
    }),
    /** Breakdowns of the rows a view's filters match (compact dashboards above a list). */
    getFacets: build.query<FacetsResult, { entity: ListEntity; request: FacetsRequest }>({
      query: ({ entity, request }) => ({
        url: `/api/portal/${entity}/facets`,
        method: 'POST',
        body: request,
      }),
      ...SNAPSHOT_DATA,
    }),
    getSubscription: build.query<SubscriptionDetail, string>({
      query: (id) => `/api/portal/subscriptions/${encodeURIComponent(id)}`,
      ...SNAPSHOT_DATA,
    }),
    getUser: build.query<UserDetail, string>({
      query: (id) => `/api/portal/users/${encodeURIComponent(id)}`,
      ...SNAPSHOT_DATA,
    }),
    getApp: build.query<AppDetail, string>({
      query: (id) => `/api/portal/apps/${encodeURIComponent(id)}`,
      ...SNAPSHOT_DATA,
    }),
    /** BI tab of an app record: usage, events, edits, invitations, hubs and legacy usage. */
    getAppBi: build.query<AppBi, string>({
      query: (id) => `/api/portal/apps/${encodeURIComponent(id)}/bi`,
      ...SNAPSHOT_DATA,
    }),
    getUsersSummary: build.query<UsersSummary, Scope>({
      query: (scope) => ({ url: '/api/portal/users/summary', params: scopeParams(scope) }),
      ...SNAPSHOT_DATA,
    }),
    getAppsSummary: build.query<AppsSummary, PeriodQuery>({
      query: (params) => ({ url: '/api/portal/apps/summary', params }),
      ...SNAPSHOT_DATA,
    }),
    getTotals: build.query<TotalsReport, { from: string; to: string }>({
      query: (params) => ({ url: '/api/portal/metrics/totals', params }),
      ...SNAPSHOT_DATA,
    }),
  }),
});

export const {
  useGetMetaQuery,
  useGetLoadStatusQuery,
  useGetCatalogQuery,
  useGetKpisQuery,
  useGetMrrSeriesQuery,
  useGetMovementsQuery,
  useGetBillingMixQuery,
  useGetLifecycleQuery,
  useGetTrialsQuery,
  useGetOffersQuery,
  useGetPaymentsQuery,
  useGetCohortsQuery,
  useGetRenewalsQuery,
  useGetFieldsQuery,
  useSearchListQuery,
  useCountViewsQuery,
  useGetFacetsQuery,
  useGetSubscriptionQuery,
  useGetUserQuery,
  useGetAppQuery,
  useGetAppBiQuery,
  useGetUsersSummaryQuery,
  useGetAppsSummaryQuery,
  useGetTotalsQuery,
} = portalApi;
